import React, {
  createContext, useContext, useState, useEffect, useCallback,
} from 'react';
import { authAPI } from '../services/api';
import { clearOrgScopeCache } from '../hooks/useOrgScope';
import { useIdleTimeout } from '@shared/hooks/useIdleTimeout';
import { SessionTimeoutWarning } from '@shared/components/Auth/SessionTimeoutWarning';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [learner, setLearner] = useState(() => {
    const saved = localStorage.getItem('scenario_os_learner');
    return saved ? JSON.parse(saved) : null;
  });
  const [loading, setLoading] = useState(false);
  // True while a visit with no stored token asks the server whether the SSO cookie (set by a TeachIQ
  // login, possibly from Championship DNA's side) is signed in. Routes wait instead of bouncing to /login.
  const [ssoPending, setSsoPending] = useState(() => !localStorage.getItem('scenario_os_token'));

  const [activeCharacter, setActiveCharacter] = useState(null);
  const [activeSpecialty, setActiveSpecialty] = useState(null);

  const isAuthenticated = !!learner;
  const COACH_ROLES_FE = new Set(['coach_learner', 'coach', 'admin', 'administrator', 'owner', 'platform_admin']);
  const ADMIN_ROLES_FE = new Set(['admin', 'administrator', 'owner', 'platform_admin']);
  const isCoach = COACH_ROLES_FE.has(learner?.role);
  const isAdmin = ADMIN_ROLES_FE.has(learner?.role);
  const isOwner = learner?.role === 'owner' || learner?.role === 'platform_admin';
  const isPlatformAdmin = learner?.role === 'platform_admin';
  // Phase-1A: the LearnerProfile API now returns tenant_id +
  // authoring_domains alongside enrolled_domains.  Surface them through
  // the context so every coach UI page can scope itself with one read
  // instead of refetching /profile.  Defaults keep older bearer tokens
  // (no tenant claim, no authoring_domains list) working.
  const tenantId = learner?.tenant_id || 'scenario_os';
  const enrolledDomains = Array.isArray(learner?.enrolled_domains) ? learner.enrolled_domains : [];
  const authoringDomains = Array.isArray(learner?.authoring_domains) ? learner.authoring_domains : [];
  const domainTiers = learner?.domain_tiers || {};
  // role_definitions.ui_access — drives coach nav (Role Management page edits these)
  const [uiAccess, setUiAccess] = useState(null);
  useEffect(() => {
    if (!learner?.role) {
      setUiAccess(null);
      return;
    }
    const token = localStorage.getItem('scenario_os_token');
    if (!token) return;
    const roleId = learner.role === 'coach_learner' ? 'coach' : learner.role;
    let cancelled = false;
    fetch(`/scenario-os/admin/roles/${encodeURIComponent(roleId)}`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((r) => (r.ok ? r.json() : null))
      .then((data) => {
        if (!cancelled && data?.ui_access) setUiAccess(data.ui_access);
      })
      .catch(() => { /* non-fatal — CoachLayout falls back to role flags */ });
    return () => { cancelled = true; };
  }, [learner?.role]);
  /** True when ui_access grants the key (true / view_only / read_only / restricted). */
  const hasUiAccess = useCallback((key) => {
    if (!uiAccess || !(key in uiAccess)) return null; // unknown → caller uses role fallback
    const v = uiAccess[key];
    return v !== false && v != null;
  }, [uiAccess]);
  // platform_admin may author any domain; owner/administrator/coach are
  // limited to their JWT authoring_domains (org/domain scope).
  // AI consolidation: entitlement for `ai` (or any legacy AI alias) grants
  // all AI aliases (ai_beginner / ai_orchestrator / ai_entrepreneur).
  const AI_ALIASES = new Set(['ai', 'ai_beginner', 'ai_orchestrator', 'ai_entrepreneur']);
  const canAuthorDomain = (domainId) => {
    if (!domainId) return false;
    if (isPlatformAdmin) return true;
    if (authoringDomains.includes(domainId)) return true;
    if (AI_ALIASES.has(domainId) && authoringDomains.some((d) => AI_ALIASES.has(d))) {
      return true;
    }
    return false;
  };
  const isEnrolledIn = (domainId) => {
    if (!domainId) return false;
    if (canAuthorDomain(domainId)) return true;
    if (enrolledDomains.includes(domainId)) return true;
    // AI consolidation: enrolled in ``ai`` covers legacy pack domain ids.
    if (AI_ALIASES.has(domainId) && enrolledDomains.some((d) => AI_ALIASES.has(d))) {
      return true;
    }
    return false;
  };
  const getTier = (domainId) => {
    if (isAdmin || canAuthorDomain(domainId)) return 'paid';
    const candidates = AI_ALIASES.has(domainId) ? [...AI_ALIASES] : [domainId];
    for (const id of candidates) {
      const tier = domainTiers[id];
      if (tier === 'active' || tier === 'trial' || tier === 'paid') return 'paid';
    }
    return 'free';
  };
  const isPaidFor = (domainId) => getTier(domainId) === 'paid';

  const logout = useCallback(() => {
    authAPI.ssoLogout().catch(() => { /* cookie may be off or already gone */ });
    localStorage.removeItem('scenario_os_token');
    localStorage.removeItem('scenario_os_learner');
    clearOrgScopeCache();
    setLearner(null);
    setActiveCharacter(null);
    setActiveSpecialty(null);
  }, []);

  const refreshProfile = useCallback(async () => {
    try {
      const { data } = await authAPI.getProfile();
      localStorage.setItem('scenario_os_learner', JSON.stringify(data));
      setLearner(data);
    } catch {
      logout();
    }
  }, [logout]);

  // Re-mint the JWT from the server so domain_tiers are always current.
  // Called on app startup and can be called by pages after a coach tier change.
  const refreshToken = useCallback(async () => {
    try {
      const { data } = await authAPI.refreshToken();
      if (data?.access_token) {
        localStorage.setItem('scenario_os_token', data.access_token);
        // Immediately reload the profile so domain_tiers in React state updates
        await refreshProfile();
      }
    } catch {
      // Non-fatal — the existing token continues to work
    }
  }, [refreshProfile]);

  useEffect(() => {
    const t = localStorage.getItem('scenario_os_token');
    if (!t) {
      authAPI.ssoSession()
        .then(async ({ data }) => {
          if (!data?.access_token) return;
          localStorage.setItem('scenario_os_token', data.access_token);
          const profile = await authAPI.getProfile();
          const merged = {
            ...profile.data,
            password_change_required: Boolean(
              profile.data.password_change_required ?? data.password_change_required,
            ),
          };
          localStorage.setItem('scenario_os_learner', JSON.stringify(merged));
          setLearner(merged);
        })
        .catch(() => {
          localStorage.removeItem('scenario_os_token');
        })
        .finally(() => setSsoPending(false));
    } else {
      // Refresh the JWT first so domain_tiers reflect any coach-granted upgrades,
      // then load the profile.
      refreshToken().catch(() => refreshProfile());
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const login = useCallback(async (email, password) => {
    const { data } = await authAPI.login({ email, password });
    localStorage.setItem('scenario_os_token', data.access_token);
    const profile = await authAPI.getProfile();
    const merged = {
      ...profile.data,
      password_change_required: Boolean(
        profile.data.password_change_required ?? data.password_change_required,
      ),
    };
    localStorage.setItem('scenario_os_learner', JSON.stringify(merged));
    setLearner(merged);
    return merged;
  }, []);

  const register = useCallback(async (formData) => {
    const { data } = await authAPI.register(formData);
    localStorage.setItem('scenario_os_token', data.access_token);
    const profile = await authAPI.getProfile();
    const merged = {
      ...profile.data,
      password_change_required: Boolean(
        profile.data.password_change_required ?? data.password_change_required,
      ),
    };
    localStorage.setItem('scenario_os_learner', JSON.stringify(merged));
    setLearner(merged);
    return merged;
  }, []);

  const changePassword = useCallback(async (currentPassword, newPassword) => {
    try {
      const { data } = await authAPI.changePassword(currentPassword, newPassword);
      if (data?.access_token) {
        localStorage.setItem('scenario_os_token', data.access_token);
      }
      await refreshProfile();
      return { success: true };
    } catch (err) {
      const d = err?.response?.data?.detail;
      const msg = typeof d === 'string' ? d : (err?.message || 'Password change failed');
      return { success: false, error: msg };
    }
  }, [refreshProfile]);

  // ── Idle session timeout ─────────────────────────────────────────────────
  // Default: 30 min idle → logout, 2 min warning.
  // Override via GET /api/v1/platform/session-config?portal=learner
  const [idleTimeoutMs, setIdleTimeoutMs] = useState(30 * 60 * 1000);
  const [warningMs, setWarningMs]         = useState(2  * 60 * 1000);

  useEffect(() => {
    fetch('/api/v1/platform/session-config?portal=learner')
      .then((r) => r.ok ? r.json() : null)
      .catch(() => null)
      .then((cfg) => {
        if (cfg?.idle_timeout_ms)   setIdleTimeoutMs(cfg.idle_timeout_ms);
        if (cfg?.warning_before_ms) setWarningMs(cfg.warning_before_ms);
      });
  }, []);

  const { isWarning, countdown, extendSession } = useIdleTimeout({
    timeoutMs:  idleTimeoutMs,
    warningMs:  warningMs,
    onTimeout:  logout,
    enabled:    isAuthenticated,
  });
  // ─────────────────────────────────────────────────────────────────────────

  // Expose the raw JWT so pages that call fetch() directly (e.g. metricsAPI,
  // usageAPI) can build Authorization headers without touching localStorage.
  const token = localStorage.getItem('scenario_os_token') || null;

  return (
    <>
      <SessionTimeoutWarning
        isWarning={isWarning}
        countdown={countdown}
        onExtend={extendSession}
        onLogout={logout}
        portalName="TeachIQ"
      />
      <AuthContext.Provider value={{
      learner,
      token,
      isAuthenticated,
      isCoach,
      isAdmin,
      isOwner,
      isPlatformAdmin,
      uiAccess,
      hasUiAccess,
      tenantId,
      enrolledDomains,
      authoringDomains,
      domainTiers,
      canAuthorDomain,
      isEnrolledIn,
      getTier,
      isPaidFor,
      loading,
      ssoPending,
      login,
      register,
      logout,
      refreshProfile,
      refreshToken,
      changePassword,
      activeCharacter,
      setActiveCharacter,
      activeSpecialty,
      setActiveSpecialty,
    }}>
        {children}
      </AuthContext.Provider>
    </>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}
